import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from './ui';
import './QueryBar.css';
import { useTranslation } from 'react-i18next';

interface QueryBarProps {
  apiUrl: string;
  onSearch: (query: string) => void;
  placeholder?: string;
  initialQuery?: string;
}

interface AuditField {
  name: string;
  type: 'text' | 'number' | 'time';
  values?: string[];
}

const STORAGE_KEY = 'ztai_audit_queries';
const MAX_RECENT = 10;

// Static field list so content assist works even before /fields loads (or if the
// backend is unreachable). Merged with/overridden by the live field metadata.
const FALLBACK_FIELDS: AuditField[] = [
  { name: 'timestamp', type: 'time' },
  { name: 'service', type: 'text' },
  { name: 'tool_name', type: 'text' },
  { name: 'status', type: 'text', values: ['success', 'error', 'policy_denied', 'policy_allowed', 'rate_limit_exceeded', 'pii_detected'] },
  { name: 'agent_id', type: 'text' },
  { name: 'org_id', type: 'text' },
  { name: 'response_status', type: 'text', values: ['200', '200_redacted', '429', '500', 'allow', 'deny'] },
  { name: 'result', type: 'text' },
  { name: 'arguments', type: 'text' },
  { name: 'id', type: 'number' },
  { name: 'earliest', type: 'time' },
  { name: 'latest', type: 'time' },
];

// Time fields accept these relative shorthands.
const TIME_VALUES = ['now', '1h', '24h', '7d', '30d', '90m'];

const FALLBACK_EXAMPLES = [
  'earliest=24h latest=now',
  'status=error',
  'status!=success',
  'status IN (policy_denied, rate_limit_exceeded)',
  'result=*rate limit*',
];

const QueryBar: React.FC<QueryBarProps> = ({ apiUrl, onSearch, placeholder = 'Search logs (e.g. status=error service=database-tools)', initialQuery = '' }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [fields, setFields] = useState<AuditField[]>(FALLBACK_FIELDS);
  const [examples, setExamples] = useState<string[]>(FALLBACK_EXAMPLES);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saved, setSaved] = useState<string[]>([]);
  const [justSaved, setJustSaved] = useState(false);
  const [showSyntaxHelp, setShowSyntaxHelp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load fields and examples from the backend
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await fetch(`${apiUrl}/admin/audit-logs/fields`);
        if (res.ok) {
          const data = await res.json();
          setFields((data.fields && data.fields.length ? data.fields : FALLBACK_FIELDS));
          setExamples((data.examples && data.examples.length ? data.examples : FALLBACK_EXAMPLES));
        }
      } catch (e) {
        // Backend unreachable — fall back to static metadata so assist still works.
        setFields(FALLBACK_FIELDS);
        setExamples(FALLBACK_EXAMPLES);
      }
    };
    fetchFields();
  }, [apiUrl]);

  // Load saved queries from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSaved(Array.isArray(parsed) ? parsed : []);
      } catch {
        setSaved([]);
      }
    }
  }, []);

  // Generate autocomplete suggestions based on the current token
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const tokens = query.trim().split(/\s+/);
    const lastToken = tokens[tokens.length - 1];

    // If the last token contains "=" (or "!="), suggest values for that field
    if (lastToken.includes('=')) {
      const fieldPart = lastToken.split(/!?=/)[0];
      const valuePart = lastToken.slice(lastToken.indexOf('=') + 1);
      const fp = fieldPart.toLowerCase();
      if (fp === 'earliest' || fp === 'latest') {
        // Time fields: suggest relative shorthands (now, 24h, 7d, …)
        setSuggestions(TIME_VALUES.filter((v) => v.startsWith(valuePart.toLowerCase())));
      } else {
        const field = fields.find((f) => f.name.toLowerCase() === fp);
        if (field && field.values) {
          const matches = field.values.filter((v) => v.toLowerCase().includes(valuePart.toLowerCase())).slice(0, 6);
          setSuggestions(matches);
        } else {
          setSuggestions([]);
        }
      }
    } else {
      // Suggest field names that start with the last token
      const names = Array.from(new Set([...fields.map((f) => f.name), 'earliest', 'latest']));
      setSuggestions(names.filter((n) => n.toLowerCase().startsWith(lastToken.toLowerCase())).slice(0, 8));
    }
  }, [query, fields]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    const tokens = query.trim().split(/\s+/);
    const lastToken = tokens[tokens.length - 1];
    let newQuery: string;

    if (lastToken.includes('=')) {
      const [fieldPart] = lastToken.split('=');
      newQuery = tokens.slice(0, -1).join(' ') + (tokens.length > 1 ? ' ' : '') + `${fieldPart}=${suggestion}`;
    } else {
      newQuery = tokens.slice(0, -1).join(' ') + (tokens.length > 1 ? ' ' : '') + suggestion + '=';
    }
    setQuery(newQuery);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleExampleClick = (ex: string) => {
    setQuery(ex);
    setShowDropdown(false);
    // Auto-submit
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
      onSearch(ex);
    }, 0);
  };

  // Accept an explicit query so saved/recent clicks don't race the async setQuery.
  const handleSearch = (q: string = query) => {
    setShowDropdown(false);
    onSearch(q);
    // Add to recent queries
    if (q.trim()) {
      const recent = (localStorage.getItem(STORAGE_KEY + '_recent') || '').split('\n').filter(Boolean);
      const updated = [q, ...recent.filter((r) => r !== q)].slice(0, MAX_RECENT);
      localStorage.setItem(STORAGE_KEY + '_recent', updated.join('\n'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    onSearch('');
    setShowDropdown(false);
  };

  const saveQuery = () => {
    if (!query.trim()) return;
    if (!saved.includes(query)) {
      const updated = [...saved, query];
      setSaved(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
    // Confirm visually and reveal the Saved list.
    setJustSaved(true);
    setShowDropdown(true);
    setTimeout(() => setJustSaved(false), 1500);
  };

  const deleteQuery = (q: string) => {
    const updated = saved.filter((s) => s !== q);
    setSaved(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const recentQueries = useMemo(() => {
    const recent = (localStorage.getItem(STORAGE_KEY + '_recent') || '').split('\n').filter(Boolean);
    return recent.slice(0, 3);
  }, []);

  return (
    <div className="query-bar">
      <div className="query-bar-input-group">
        <input
          ref={inputRef}
          type="text"
          className="query-bar-input"
          placeholder={placeholder}
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
        />
        {query && <button className="query-bar-clear" onClick={handleClear} title={t('components.queryBar.clear')}><Icon name="close" size={16} /></button>}
        <button className="query-bar-btn" onClick={() => handleSearch()} title={t('components.queryBar.search')}><Icon name="search" size={16} /></button>

      {showDropdown && (suggestions.length > 0 || examples.length > 0 || recentQueries.length > 0 || saved.length > 0) && (
        <div className="query-bar-dropdown" ref={dropdownRef}>
          {suggestions.length > 0 && (
            <div className="query-bar-section">
              <div className="query-bar-section-title">{t('components.queryBar.suggestions')}</div>
              {suggestions.map((s) => (
                <div key={s} className="query-bar-item" onClick={() => handleSuggestionClick(s)}>
                  {s}
                </div>
              ))}
            </div>
          )}

          {examples.length > 0 && (
            <div className="query-bar-section">
              <div className="query-bar-section-title">{t('components.queryBar.examples')}</div>
              {examples.map((ex) => (
                <div key={ex} className="query-bar-item" onClick={() => handleExampleClick(ex)}>
                  <Icon name="chevronRight" size={12} />
                  {ex}
                </div>
              ))}
            </div>
          )}

          {recentQueries.length > 0 && (
            <div className="query-bar-section">
              <div className="query-bar-section-title">{t('components.queryBar.recent')}</div>
              {recentQueries.map((q) => (
                <div key={q} className="query-bar-item" onClick={() => { setQuery(q); handleSearch(q); }}>
                  <Icon name="refresh" size={12} />
                  {q.length > 50 ? q.slice(0, 50) + '…' : q}
                </div>
              ))}
            </div>
          )}

          {saved.length > 0 && (
            <div className="query-bar-section">
              <div className="query-bar-section-title">{t('components.queryBar.saved')}</div>
              {saved.map((q) => (
                <div key={q} className="query-bar-item">
                  <div style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => { setQuery(q); handleSearch(q); }}>
                    <Icon name="checkBadge" size={12} />
                    {q.length > 50 ? q.slice(0, 50) + '…' : q}
                  </div>
                  <button className="query-bar-delete" onClick={() => deleteQuery(q)} title={t('components.queryBar.delete')}><Icon name="trash" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      <div className="query-bar-footer">
        {query && (
          <button className="query-bar-save-btn" onClick={saveQuery} title={t('components.queryBar.saveThisQuery')}>
            <Icon name="checkBadge" size={13} />
            {justSaved ? t('components.queryBar.saved2') : t('components.queryBar.save')}
          </button>
        )}
        <button className="query-bar-help-btn" onClick={() => setShowSyntaxHelp(!showSyntaxHelp)} title={t('components.queryBar.querySyntaxHelp')}>
          <Icon name="info" size={13} />
        </button>
      </div>

      {showSyntaxHelp && (
        <div className="query-bar-help">
          <strong>{t('components.queryBar.splunkStyleSyntax')}</strong>
          <ul>
            <li><code>status=error</code> {t('components.queryBar.exactMatch')}</li>
            <li><code>status!=success</code> {t('components.queryBar.notEqual')}</li>
            <li><code>status IN (error,policy_denied)</code> {t('components.queryBar.multipleValues')}</li>
            <li><code>result=*rate limit*</code> {t('components.queryBar.wildcardLIKE')}</li>
            <li><code>earliest=24h latest=now</code> {t('components.queryBar.timeRanges24h7d90m30d')}</li>
            <li><code>status=error OR status=policy_denied</code> {t('components.queryBar.orJoinsSpacesAND')}</li>
            <li>{t('components.queryBar.bareWordsSearchAllFields')}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default QueryBar;
